import consola from 'consola';

/**
 * Validate that a form value is not empty.
 */
export function required(value) {
  if (value === undefined || value === null) {
    return false;
  }

  if (Array.isArray(value)) {
    return !!value.length;
  }

  if (typeof value === 'object') {
    // eslint-disable-next-line no-unreachable-loop
    for (const _ in value) {
      return true;
    }
    return false;
  }

  return !!String(value).trim().length;
}

export function validEmail(value) {
  const re
    = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[\d{1,3}\.\d{1,3}\.\d{1,3}\.\d{1,3}\])|(([a-z\-0-9]+\.)+[a-z]{2,}))$/i;
  return re.test(value);
}

export function validUrl(value) {
  return value && typeof value === 'string' && /^https?:\/\//i.test(value);
}

/**
 * Returns an absolute http(s) URL safe for href/window.open, or null when invalid.
 * @param {string} url
 * @returns {string|null} Parsed absolute http(s) URL, or null when invalid
 */
export function getSafeHttpUrl(url) {
  if (!validUrl(url)) {
    return null;
  }

  try {
    const parsedUrl = new URL(url);

    if (parsedUrl.protocol !== 'http:' && parsedUrl.protocol !== 'https:') {
      return null;
    }

    return parsedUrl.toString();
  } catch {
    return null;
  }
}

const forbiddenNameCharacters = /[<>"&\r\n]/;
const regexExpEmojies
  = /([\u00A9\u00AE\u2000-\u3300]|\uD83C[\uD000-\uDFFF]|\uD83D[\uD000-\uDFFF]|\uD83E[\uD000-\uDFFF])/i;

export function normalizeFullName(value) {
  return value.trim().replace(/\s+/g, ' ');
}

export function validName(value) {
  if (!value) {
    return false;
  }

  if (forbiddenNameCharacters.test(value)) {
    return false;
  }

  const normalized = normalizeFullName(value);

  if (!normalized) {
    return false;
  }

  const words = normalized.split(' ');

  return words.length > 1 && words[1].length > 0 && !regexExpEmojies.test(normalized);
}

export function validPassword(value) {
  if (!value) {
    return false;
  }

  const hasCorrectLength = value.length >= 8 && value.length <= 255;
  const hasLowerCaseLetter = value.toUpperCase() !== value;
  const hasUpperCaseLetter = value.toLowerCase() !== value;
  const hasDigit = /\d/.test(value);

  return hasCorrectLength && hasLowerCaseLetter && hasUpperCaseLetter && hasDigit;
}

export function isNull(value) {
  return value === null;
}

export function isString(value) {
  return typeof value === 'string';
}

export function validateForm(config, values) {
  const errors = {};
  let isValid = true;

  for (const [fieldName, rules] of Object.entries(config)) {
    const fieldValue = values[fieldName];
    const firstInvalidRule = rules.find((rule) => rule(fieldValue, values));

    errors[fieldName] = firstInvalidRule?.(fieldValue, values) || null;

    if (errors[fieldName] !== null) {
      isValid = false;
    }
  }

  return {
    errors,
    isValid,
  };
}

export async function validateJWTs(jwts, $jwt, $jwks) {
  function jwkFetcher(header, callback) {
    $jwks.getSigningKey(header.kid, (err, key) => {
      if (err || !key) {
        consola.error(`### Error during fetching JWKS: ${err ? err.message : 'no signing key'}`);
        callback(err || new Error('No signing key available'));
        return;
      }
      const signingKey = key.publicKey || key.rsaPublicKey;
      callback(null, signingKey);
    });
  }

  const verifyOne = (jwt) =>
    new Promise((resolve, reject) => {
      $jwt.verify(jwt, jwkFetcher, { algorithms: ['RS256'] }, (err) => {
        if (err) {
          consola.error(`### Error during verifying tokens: ${err.message}`);
          reject(new Error(err.message));
          return;
        }
        resolve();
      });
    });

  await Promise.all(jwts.map(verifyOne));
}

/**
 * Validates and sanitizes a auth0-callback redirect URL to prevent XSS attacks.
 * Only allows HTTP/HTTPS URLs from hostelworld.com domains.
 *
 * @param {string} url - The URL to validate
 * @param {string} fallbackUrl - Fallback URL to return if validation fails
 * @returns {string|null} - Validated URL or fallback URL if invalid, null if no fallback provided
 */
export function validateCallbackRedirectUrl(url, fallbackUrl = null) {
  if (!url || typeof url !== 'string') {
    return fallbackUrl;
  }

  try {
    const parsedUrl = new URL(url);

    if (parsedUrl.protocol !== 'http:' && parsedUrl.protocol !== 'https:') {
      return fallbackUrl;
    }

    const allowedDomainPattern = /^([a-z0-9-]+\.)*hostelworld\.com$/i;
    if (!allowedDomainPattern.test(parsedUrl.hostname)) {
      return fallbackUrl;
    }

    return url;
  } catch {
    return fallbackUrl;
  }
}
